import React, { useRef, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';

import { isValidCode, shapeProblem, SHAPE_HINT, normalizeCode } from './codeShape';

// Create codes, three ways, all landing in the one codes table (Admin's
// modes, kept: single / mass by prefix; the word list mode retired 2026-09-03,
// controls anywhere (Gwen 2026-08-24): a code admits only while its wave
// is open — the wave's start and cutoff times do all the gating. Max uses
// locks at creation; the codes table says so where the number lives.
const CODE_CLASSES = ['creator', 'vvip', 'public', 'internal'];

const ClassSelect = ({ value, onChange, label = 'Class' }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <Form.Select aria-label={label} value={value} onChange={e => onChange(e.target.value)}>
      {CODE_CLASSES.map(c => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

/** The landed report, in the server's own buckets — every line answered. */
export const LandedReport = ({ report }) => {
  if (!report) return null;
  const created = report.created ?? [];
  const skipped = report.skippedExisting ?? [];
  const rejected = report.rejected ?? [];
  return (
    <Alert variant={rejected.length ? 'warning' : 'success'} className="my-2">
      <b>{created.length} created</b>
      {created.length > 0 && <> ({created.join(', ')})</>}
      {skipped.length > 0 && (
        <>
          {' · '}
          {skipped.length} skipped: already exists ({skipped.join(', ')})
        </>
      )}
      {rejected.length > 0 && (
        <>
          {' · '}
          <span className="text-danger">
            {rejected.length} rejected: {rejected.map(r => `${r.code}: ${r.reason}`).join('; ')}
          </span>
        </>
      )}
    </Alert>
  );
};

const CreateCodesPanel = ({ onMint }) => {
  const [single, setSingle] = useState({
    code: '',
    maxUses: 100,
    classLabel: 'public',
    ownerLabel: '',
  });
  const [mass, setMass] = useState({
    prefix: '',
    count: 10,
    usesPerCode: 50,
    classLabel: 'public',
    ownerLabel: '',
  });
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const submit = async (body, clientRejected = []) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    setReport(null);
    try {
      const result = await onMint(body);
      const landed = result?.report ?? null;
      // Client-side rejects merge into the DISPLAYED report only — they
      // never ride the wire (the server's request schema is strict, and
      // an unknown key fails the whole mint; that bug ate the first
      // word-list attempt, Gwen 2026-08-27).
      setReport(
        landed === null && clientRejected.length === 0
          ? null
          : {
              created: landed?.created ?? [],
              skippedExisting: landed?.skippedExisting ?? [],
              rejected: [...(landed?.rejected ?? []), ...clientRejected],
            }
      );
    } catch (err) {
      console.error('Waitlist mint failed:', err);
      const body_ = err?.response?.data;
      const message = body_?.error?.message ?? body_?.message ?? null;
      setError(message ? `Mint failed: ${message}` : 'Mint failed: nothing was created.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const submitSingle = () => {
    const problem = shapeProblem(single.code);
    if (problem) {
      setReport({
        created: [],
        skippedExisting: [],
        rejected: [{ code: normalizeCode(single.code) || '(empty)', reason: problem }],
      });
      return;
    }
    submit({
      mode: 'single',
      code: normalizeCode(single.code),
      maxUses: Number(single.maxUses),
      classLabel: single.classLabel,
      ownerLabel: single.ownerLabel.trim(),
    });
  };

  const submitMass = () => {
    if (!isValidCode(mass.prefix)) {
      setReport({
        created: [],
        skippedExisting: [],
        rejected: [
          { code: normalizeCode(mass.prefix) || '(empty)', reason: shapeProblem(mass.prefix) },
        ],
      });
      return;
    }
    submit({
      mode: 'mass',
      prefix: normalizeCode(mass.prefix),
      count: Number(mass.count),
      usesPerCode: Number(mass.usesPerCode),
      classLabel: mass.classLabel,
      ownerLabel: mass.ownerLabel.trim(),
    });
  };

  return (
    <div className="waitlist-panel">
      <h5>Create Invite Codes</h5>
      <div className="text-muted small mb-2">
        Two ways, both land in the same table. Code shape at every door: <b>{SHAPE_HINT}</b>;
        lowercase is normalized up. Max uses locks at creation.
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <LandedReport report={report} />

      <Row className="g-2 align-items-end">
        <Col md={3}>
          <Form.Group controlId="waitlist-single-code">
            <Form.Label>One code</Form.Label>
            <Form.Control
              placeholder="SUGARRUSH"
              value={single.code}
              onChange={e => setSingle({ ...single, code: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Max uses</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={single.maxUses}
              onChange={e => setSingle({ ...single, maxUses: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <ClassSelect
            value={single.classLabel}
            onChange={v => setSingle({ ...single, classLabel: v })}
          />
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Attributed to</Form.Label>
            <Form.Control
              value={single.ownerLabel}
              onChange={e => setSingle({ ...single, ownerLabel: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}></Col>
        <Col md={1}>
          <Button onClick={submitSingle} disabled={submitting}>
            Create
          </Button>
        </Col>
      </Row>

      <Row className="g-2 align-items-end mt-1">
        <Col md={3}>
          <Form.Group controlId="waitlist-mass-prefix">
            <Form.Label>Mass generate: prefix</Form.Label>
            <Form.Control
              value={mass.prefix}
              onChange={e => setMass({ ...mass, prefix: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>How many</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={mass.count}
              onChange={e => setMass({ ...mass, count: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Uses / code</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={mass.usesPerCode}
              onChange={e => setMass({ ...mass, usesPerCode: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Attributed to</Form.Label>
            <Form.Control
              value={mass.ownerLabel}
              onChange={e => setMass({ ...mass, ownerLabel: e.target.value })}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <ClassSelect
            value={mass.classLabel}
            onChange={v => setMass({ ...mass, classLabel: v })}
          />
        </Col>
        <Col md={1}>
          <Button onClick={submitMass} disabled={submitting}>
            Mint batch
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default CreateCodesPanel;
